import { FC, useEffect, useState } from 'react';
import { X, MapPin, Calendar, User, Building } from 'lucide-react';
import { Customer } from '../lib/types';
import { resolveUserName } from '../lib/directus';
import { CustomerStatusBadge, CustomerPriorityBadge } from './ui/CustomerBadge';
import { useLanguage } from '../contexts/LanguageContext';

interface CustomerDetailModalProps {
  customer: Customer | null;
  isOpen: boolean;
  onClose: () => void;
  onStartVisit?: (customer: Customer) => void;
}

export const CustomerDetailModal: FC<CustomerDetailModalProps> = ({
  customer,
  isOpen,
  onClose,
  onStartVisit
}) => {
  const { t } = useLanguage();
  const [contactPersonName, setContactPersonName] = useState<string>('');

  // Resolve contact person UUID to name
  useEffect(() => {
    if (customer?.contact_person) {
      resolveUserName(customer.contact_person)
        .then(name => setContactPersonName(name))
        .catch(error => {
          console.warn('Failed to resolve contact person name:', error);
          setContactPersonName(customer.contact_person); // Fallback to original value
        });
    } else {
      setContactPersonName('');
    }
  }, [customer?.contact_person]);

  if (!isOpen || !customer) return null;

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return t('customer_details.no_data');
    return new Date(dateString).toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };


  return (
    <div className="fixed inset-0 bg-black bg-opacity-20 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold text-muted-foreground">{t('customer_details.modal_title')}</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Customer Name & ID */}
          <div className="text-center pb-4 border-b">
            <h3 className="text-xl font-bold text-muted-foreground mb-1">{customer.name}</h3>
            <p className="text-sm text-muted-foreground">ID: {customer.idcust || customer.id}</p>
          </div>

          {/* Status & Priority */}
          <div className="flex space-x-2">
            <CustomerStatusBadge status={customer.status} />
            <CustomerPriorityBadge priority={customer.priority} />
          </div>

          {/* Contact Information */}
          <div className="space-y-3">
            <div className="flex items-start space-x-3">
              <MapPin className="w-5 h-5 text-muted-foreground mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-muted-foreground">{t('customer_details.address')}</p>
                <p className="text-sm text-muted-foreground">{customer.address || t('customer_details.no_address')}</p>
              </div>
            </div>

            {customer.contact_person && (
              <div className="flex items-start space-x-3">
                <User className="w-5 h-5 text-muted-foreground mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-muted-foreground">{t('customer_details.contact_person')}</p>
                  <p className="text-sm text-muted-foreground">
                    {contactPersonName || customer.contact_person || t('customer_details.not_assigned')}
                  </p>
                </div>
              </div>
            )}

            {customer.ukuranTambak && (
              <div className="flex items-start space-x-3">
                <Building className="w-5 h-5 text-muted-foreground mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-muted-foreground">{t('customer_details.farm_size')}</p>
                  <p className="text-sm text-muted-foreground">{customer.ukuranTambak}</p>
                </div>
              </div>
            )}

            {customer.tahunDibangun && (
              <div className="flex items-start space-x-3">
                <Calendar className="w-5 h-5 text-muted-foreground mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-muted-foreground">{t('customer_details.year_built')}</p>
                  <p className="text-sm text-muted-foreground">{customer.tahunDibangun}</p>
                </div>
              </div>
            )}
          </div>

          {/* Visit History */}
          <div className="space-y-3 pt-4 border-t">
            <h4 className="font-medium text-muted-foreground">{t('customer_details.visit_history')}</h4>

            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t('customer_details.last_visit')}:</span>
                <span className="text-muted-foreground">{formatDate(customer.tglInteraksiTerakhir)}</span>
              </div>

              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t('customer_details.last_purchase')}:</span>
                <span className="text-muted-foreground">{formatDate(customer.tglPembelianTerakhir)}</span>
              </div>

              {customer.revenue && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t('customer_details.revenue')}:</span>
                  <span className="text-status-success font-medium">{customer.revenue}</span>
                </div>
              )}
            </div>
          </div>

          {/* Additional Info */}
          {customer.Description && (
            <div className="pt-4 border-t">
              <h4 className="font-medium text-muted-foreground mb-2">{t('customer_details.description')}</h4>
              <p className="text-sm text-muted-foreground">{customer.Description}</p>
            </div>
          )}

          {customer.source && (
            <div className="pt-4 border-t">
              <h4 className="font-medium text-muted-foreground mb-2">{t('customer_details.source')}</h4>
              <p className="text-sm text-muted-foreground">{customer.source}</p>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="p-4 border-t space-y-3">
          {onStartVisit && (
            <button
              onClick={() => {
                onStartVisit(customer);
                onClose();
              }}
              className="w-full bg-primary text-primary-foreground py-3 rounded-lg font-medium hover:bg-primary/90 transition-colors"
            >
              🚀 {t('customer_details.start_visit')}
            </button>
          )}

        </div>
      </div>
    </div>
  );
}; 
